<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import {
  ArrowLeft,
  CalendarClock,
  Files,
  FolderKanban,
  Layers,
  LayoutDashboard,
  Link2,
  ListTodo,
  NotebookPen,
  Trash2,
  UsersRound,
} from '@lucide/vue';
import StatoErrore from '@/components/ui/StatoErrore.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import VistaTask from '@/features/task/components/VistaTask.vue';
import PanoramicaSezione from '../../components/PanoramicaSezione.vue';
import FasiSezione from '../../components/FasiSezione.vue';
import ScadenzeSezione from '../../components/ScadenzeSezione.vue';
import NoteSezione from '../../components/NoteSezione.vue';
import TeamSezione from '../../components/TeamSezione.vue';
import RiferimentiSezione from '../../components/RiferimentiSezione.vue';
import FileSezione from '../../components/FileSezione.vue';
import { useAvvisi } from '@/composables/useAvvisi';
import { useConferma } from '@/composables/useConferma';
import { useAuthStore } from '@/stores/auth';
import { EvolfinApiError } from '@/api/client';
import { useActiveTab } from '@/composables/useActiveTab';
import { useActiveTabScroll } from '@/composables/useActiveTabScroll';
import { projectKeys } from '../../query-keys';
import { dettaglioProgetto, eliminaProgetto } from '../../api';
import { coloriStatoProgetto } from '../../stati';

definePage({ meta: { titolo: 'Progetti' } });

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const avvisi = useAvvisi();
const conferma = useConferma();

const puoEliminare = computed(() => auth.ha('can_delete_progetti'));

const progettoId = computed(() => Number((route.params as { id: string }).id));

const {
  data: progetto,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: computed(() => projectKeys.detail(progettoId.value)),
  queryFn: () => dettaglioProgetto(progettoId.value),
  // È la pagina su cui si lavora: rimontandola si riparte dal server, non da
  // una copia di trenta secondi fa.
  refetchOnMount: 'always',
});

// L'ordine segue come si lavora la pratica: prima quel che si tocca ogni
// giorno (fasi, documenti, attività), poi le date e il diario, infine i dati
// che si guardano di rado. La prima tab è anche quella che si apre di default.
const tabs = [
  { chiave: 'panoramica', etichetta: 'Panoramica', icona: LayoutDashboard },
  { chiave: 'fasi', etichetta: 'Fasi', icona: Layers },
  { chiave: 'file', etichetta: 'File', icona: Files },
  { chiave: 'task', etichetta: 'Task', icona: ListTodo },
  { chiave: 'scadenze', etichetta: 'Scadenze', icona: CalendarClock },
  { chiave: 'note', etichetta: 'Note', icona: NotebookPen },
  { chiave: 'riferimenti', etichetta: 'Riferimenti', icona: Link2 },
  { chiave: 'team', etichetta: 'Team', icona: UsersRound },
] as const;
const tab = useActiveTab(
  tabs.map((t) => t.chiave),
  'panoramica',
);
useActiveTabScroll(tab);

async function elimina(): Promise<void> {
  if (!progetto.value) return;
  if (
    !(await conferma({
      titolo: 'Elimina progetto',
      messaggio: `Eliminare ${progetto.value.nome}? Sparirà da elenchi, ricerche e task. I documenti già caricati restano archiviati, ma il progetto non sarà più consultabile.`,
      etichettaConferma: 'Elimina',
      distruttiva: true,
    }))
  ) {
    return;
  }
  try {
    await eliminaProgetto(progettoId.value);
    avvisi.successo('Progetto eliminato');
    await router.push('/progetti');
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile eliminare', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      type="button"
      class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
      @click="router.push('/progetti')"
    >
      <ArrowLeft :size="15" :stroke-width="1.9" /> Progetti
    </button>

    <div v-if="isLoading" class="text-[13px] text-ink-muted">Caricamento…</div>

    <StatoErrore
      v-else-if="isError || !progetto"
      titolo="Progetto non trovato"
      messaggio="Il progetto richiesto non esiste o non è più disponibile."
      @riprova="refetch()"
    />

    <template v-else>
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
            aria-hidden="true"
          >
            <FolderKanban :size="24" :stroke-width="1.8" />
          </span>
          <div>
            <div class="flex flex-wrap items-center gap-2.5">
              <h1 class="font-display text-[28px] leading-tight font-semibold break-words">
                {{ progetto.nome }}
              </h1>
              <span
                class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
                :style="{
                  background: `color-mix(in oklch, ${coloriStatoProgetto[progetto.stato] ?? 'var(--color-info)'} 12%, transparent)`,
                  color: coloriStatoProgetto[progetto.stato] ?? 'var(--color-info)',
                }"
              >
                {{ progetto.stato }}
              </span>
            </div>
            <p class="mt-0.5 text-[13px] text-ink-secondary">
              <span class="tabular-nums">{{ progetto.codiceInterno }}</span>
              <span v-if="progetto.tipologiaPratica"> · {{ progetto.tipologiaPratica }}</span>
            </p>
          </div>
        </div>

        <PulsanteAzione v-if="puoEliminare" variante="contorno" @click="elimina">
          Elimina
          <template #icona><Trash2 :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
      </header>

      <!-- Barra tab -->
      <nav
        ref="tabBar"
        class="flex gap-1 overflow-x-auto border-b border-line-soft"
        aria-label="Sezioni progetto"
      >
        <button
          v-for="t in tabs"
          :key="t.chiave"
          type="button"
          class="flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
          :aria-current="tab === t.chiave ? 'true' : undefined"
          :class="
            tab === t.chiave
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-ink-secondary hover:text-ink'
          "
          @click="tab = t.chiave"
        >
          <component :is="t.icona" :size="15" :stroke-width="1.8" />
          {{ t.etichetta }}
        </button>
      </nav>

      <section>
        <PanoramicaSezione
          v-if="tab === 'panoramica'"
          :progetto="progetto"
          @cambia-tab="tab = $event"
        />
        <FasiSezione
          v-else-if="tab === 'fasi'"
          :progetto-id="progetto.id"
          :regole-fasi="progetto.regoleFasi"
        />
        <ScadenzeSezione v-else-if="tab === 'scadenze'" :progetto-id="progetto.id" />
        <NoteSezione v-else-if="tab === 'note'" :progetto-id="progetto.id" />
        <TeamSezione v-else-if="tab === 'team'" :progetto-id="progetto.id" />
        <RiferimentiSezione
          v-else-if="tab === 'riferimenti'"
          :progetto-id="progetto.id"
          :cliente-id="progetto.clienteId"
        />

        <FileSezione v-else-if="tab === 'file'" :progetto-id="progetto.id" />
        <VistaTask
          v-else-if="tab === 'task'"
          ambito="progetto"
          :progetto-id="progetto.id"
          :progetto="{ id: progetto.id, nome: progetto.nome }"
          testo-vuoto="Nessuna attività aperta su questo progetto."
        />
      </section>
    </template>
  </div>
</template>
